<template>
  <section class="marketing-section bg-surface-variant">
    <div class="marketing-section__content">
      <h2 class="text-h4 text-center font-weight-bold mb-10">How it works</h2>

      <v-row>
        <v-col v-for="(step, i) in steps" :key="i" cols="12" sm="6" md="3">
          <div class="text-center">
            <v-avatar :color="step.color" size="64" class="mb-4">
              <v-icon size="32" color="white">{{ step.icon }}</v-icon>
            </v-avatar>
            <h3 class="text-subtitle-1 font-weight-bold mb-2">
              {{ step.title }}
            </h3>
            <p class="text-body-2 text-medium-emphasis">
              {{ step.description }}
            </p>
          </div>
        </v-col>
      </v-row>

      <div class="funding-demo mt-10">
        <div class="funding-demo__bar-wrapper">
          <div class="funding-demo__threshold-marker" aria-hidden="true">
            <span class="funding-demo__threshold-label text-caption">
              Threshold
            </span>
            <div class="funding-demo__threshold-line" />
          </div>
          <v-progress-linear
            :model-value="barValue"
            :color="goalMet ? 'success' : 'primary'"
            rounded
            height="8"
          />
        </div>
        <div class="d-flex justify-space-between text-body-2 mt-2">
          <span>
            <template v-if="goalMet">
              <v-icon size="small" color="success" class="mr-1">
                mdi-check-circle
              </v-icon>
              Goal reached &mdash; {{ backerCount }} of {{ TOTAL_BACKERS }}
              backers
            </template>
            <template v-else>
              {{ backerCount }} of {{ TOTAL_BACKERS }} backers
            </template>
          </span>
          <span>{{ fundedPercentage }}% funded</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
const TOTAL_BACKERS = 20;
const THRESHOLD = 15; // 75%
const FILL_DURATION = 6000; // ms to go 0→100%
const HOLD_DURATION = 1500; // ms to hold at final value
const RESET_PAUSE = 500; // ms pause before restart
const TICK_INTERVAL = 50; // ms between updates

const backerCount = ref(0);
const barValue = ref(0); // 0–100: drives progress bar width
const fundedPercentage = computed(() =>
  THRESHOLD > 0 ? Math.round((backerCount.value / THRESHOLD) * 100) : 0,
);
const goalMet = computed(() => backerCount.value >= THRESHOLD);

let timer: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches;

  if (prefersReducedMotion) {
    backerCount.value = 20;
    barValue.value = 100;
    return;
  }

  startAnimation();
});

onUnmounted(() => {
  if (timer) clearInterval(timer);
});

function startAnimation() {
  let elapsed = 0;
  let phase: 'filling' | 'holding' | 'pausing' = 'filling';

  timer = setInterval(() => {
    if (phase === 'filling') {
      elapsed += TICK_INTERVAL;
      const progress = Math.min(elapsed / FILL_DURATION, 1);
      barValue.value = Math.round(progress * 100);
      backerCount.value = Math.round(progress * TOTAL_BACKERS);

      if (progress >= 1) {
        phase = 'holding';
        elapsed = 0;
      }
    } else if (phase === 'holding') {
      elapsed += TICK_INTERVAL;
      if (elapsed >= HOLD_DURATION) {
        phase = 'pausing';
        elapsed = 0;
        barValue.value = 0;
        backerCount.value = 0;
      }
    } else {
      elapsed += TICK_INTERVAL;
      if (elapsed >= RESET_PAUSE) {
        phase = 'filling';
        elapsed = 0;
      }
    }
  }, TICK_INTERVAL);
}

const steps = [
  {
    icon: 'mdi-file-document-edit-outline',
    color: 'primary',
    title: 'Organizer creates',
    description: 'Real venue, fixed dates, transparent budget.',
  },
  {
    icon: 'mdi-account-multiple-plus',
    color: 'secondary',
    title: 'Community commits',
    description: 'People back the event during fundraising.',
  },
  {
    icon: 'mdi-check-circle',
    color: 'tertiary',
    title: 'Threshold met \u2192 Confirmed',
    description: 'Event only happens when enough people commit and pay.',
  },
  {
    icon: 'mdi-shield-check',
    color: 'primary',
    title: "Didn't work out? No one gets burned.",
    description: "If it doesn't hit the goal, ticket prices are refunded.",
  },
];
</script>

<style scoped>
.funding-demo {
  max-width: 400px;
  margin-inline: auto;
}

.funding-demo__bar-wrapper {
  position: relative;
  padding-top: 20px;
}

.funding-demo__threshold-marker {
  position: absolute;
  left: 75%;
  top: 0;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
  z-index: 1;
}

.funding-demo__threshold-label {
  white-space: nowrap;
  color: rgb(var(--v-theme-on-surface-variant));
  opacity: 0.6;
  line-height: 1;
  margin-bottom: 2px;
}

.funding-demo__threshold-line {
  height: 16px;
  border-left: 2px dashed rgb(var(--v-theme-on-surface-variant));
  opacity: 0.6;
}
</style>
